import {
  FormControl,
  FormHelperText,
  InputLabel,
  Select,
  MenuItem,
  makeStyles,
  Collapse,
  List,
  IconButton,
  ListItemSecondaryAction,
  ListItemText,
  ListItemIcon,
  ListItem,
} from '@material-ui/core';
import Checkbox from '@material-ui/core/Checkbox';
import { Section } from 'api/rest/sections/sections.types';
import { useMemo, useState, useCallback } from 'react';
import { ControllerFieldState, ControllerRenderProps, UseFormSetValue, UseFormStateReturn } from 'react-hook-form';
import { OnGoInitializeFormValues } from './onGoInitialize.types';

import ExpandLess from '@material-ui/icons/ExpandLess';
import ExpandMore from '@material-ui/icons/ExpandMore';
import { resolvePluralLabel } from './resolvePluralLabel';
import { changeAllSectionsState, changeSectionState, changeSubsectionState } from './utils';

type ScopeDropdownProps = {
  sectionsData: Section[];
  fieldRenderProps: {
    field: ControllerRenderProps<OnGoInitializeFormValues, 'scope'>;
    fieldState: ControllerFieldState;
    formState: UseFormStateReturn<OnGoInitializeFormValues>;
  };
  setValue: UseFormSetValue<OnGoInitializeFormValues>;
};

const useStyles = makeStyles(theme => ({
  nestedLvl1: {
    paddingLeft: theme.spacing(2),
  },
  nestedLvl2: {
    paddingLeft: theme.spacing(6),
  },
}));

const LABEL = 'Dział lub poddział';

export const ScopeDropdownPlaceholder = () => {
  return (
    <FormControl size='small' fullWidth margin='normal' variant='outlined'>
      <InputLabel id='ongo-scope-label'>{LABEL}</InputLabel>
      <Select displayEmpty label={LABEL} multiple value={[]}>
        <MenuItem value=''>Wybierz dział lub poddział</MenuItem>
      </Select>
    </FormControl>
  );
};

type SectionGroupProps = {
  section: Section;
  field: ControllerRenderProps<OnGoInitializeFormValues, 'scope'>;
  commitScope: (next: OnGoInitializeFormValues['scope']) => void;
};

const SectionGroup = ({ section, field, commitScope }: SectionGroupProps) => {
  const classes = useStyles();
  const [isOpen, setIsOpen] = useState(false);

  // TODO onDoubleClick?

  const onExpand = () => {
    setIsOpen(prev => !prev);
  };

  const onSelectSectionToggle = useCallback(
    (sectionId: string | number) => () => {
      const sectionValue = field.value.sections[sectionId];
      const newState = sectionValue.allSelected === 'unchecked' || sectionValue.allSelected === 'indeterminate';
      commitScope(changeSectionState({ scope: field.value, sectionId: sectionId as string, newState }));
    },
    [commitScope, field.value],
  );

  const onSelectSubsectionToggle = useCallback(
    (sectionId: string | number, subsectionId: string | number) => () => {
      const sectionKey = sectionId as string;
      const subsectionKey = subsectionId as string;
      const newState = !field.value.sections[sectionKey].subsections[subsectionKey];
      commitScope(changeSubsectionState({ scope: field.value, sectionId: sectionKey, subsectionId: subsectionKey, newState }));
    },
    [commitScope, field.value],
  );

  const sectionSelectionState = useMemo(() => {
    const sectionValue = field.value.sections[section.id];
    return {
      checked: sectionValue.allSelected === 'checked' || sectionValue.allSelected === 'indeterminate',
      indeterminate: sectionValue?.allSelected === 'indeterminate',
    };
  }, [field, section.id]);

  return (
    <>
      <ListItem
        className={classes.nestedLvl1}
        key={`section-${section.id}`}
        role={undefined}
        dense
        button
        onClick={onSelectSectionToggle(section.id)}
      >
        <ListItemIcon>
          <Checkbox
            checked={sectionSelectionState.checked}
            indeterminate={sectionSelectionState.indeterminate}
            size='small'
            edge='start'
            tabIndex={-1}
            disableRipple
            color='primary'
            onClick={onSelectSectionToggle(section.id)}
          />
        </ListItemIcon>
        <ListItemText id={'labelId'} primary={section.name} />
        <ListItemSecondaryAction>
          {section.subsections.length > 0 && (
            <IconButton edge='end' onClick={onExpand}>
              {isOpen ? <ExpandLess /> : <ExpandMore />}
            </IconButton>
          )}
        </ListItemSecondaryAction>
      </ListItem>
      <Collapse in={isOpen} timeout='auto'>
        {section.subsections.map(subsection => (
          <List component='div' disablePadding key={`subsec-${subsection.id}`}>
            <ListItem button dense className={classes.nestedLvl2} onClick={onSelectSubsectionToggle(section.id, subsection.id)}>
              <ListItemIcon>
                <Checkbox
                  size='small'
                  edge='start'
                  tabIndex={-1}
                  color='primary'
                  checked={field.value.sections[section.id].subsections[subsection.id]}
                  disableRipple
                  onClick={onSelectSubsectionToggle(section.id, subsection.id)}
                />
              </ListItemIcon>
              <ListItemText primary={subsection.name} />
            </ListItem>
          </List>
        ))}
      </Collapse>
    </>
  );
};

const ScopeDropdown = ({ sectionsData, fieldRenderProps: { field, fieldState }, setValue }: ScopeDropdownProps) => {
  const commitScope = useCallback(
    (next: OnGoInitializeFormValues['scope']) => {
      setValue(field.name, next, { shouldDirty: true, shouldValidate: true });
    },
    [setValue, field.name],
  );

  const onSelectAllToggle = useCallback(() => {
    const newState = field.value.allSelected === 'unchecked' || field.value.allSelected === 'indeterminate';
    commitScope(changeAllSectionsState(field.value, newState));
  }, [commitScope, field.value]);

  const allSectionsState = useMemo(() => {
    return {
      checked: field.value.allSelected === 'checked' || field.value.allSelected === 'indeterminate',
      indeterminate: field.value.allSelected === 'indeterminate',
    };
  }, [field]);

  const value = useMemo(() => {
    if (field.value.allSelected === 'checked') {
      return ['Wszystkie działy'];
    }

    const result = [];
    const selected = {
      sectionsSelected: 0,
      sectionsIndeterminate: 0,
      subsectionsSelected: 0,
    };
    Object.values(field.value.sections).forEach(section => {
      if (section.allSelected === 'checked') {
        selected.sectionsSelected++;
      } else if (section.allSelected === 'indeterminate') {
        selected.sectionsIndeterminate++;
      }
      Object.values(section.subsections).forEach(subsection => {
        if (subsection) {
          selected.subsectionsSelected++;
        }
      });
    });

    if (selected.sectionsSelected) {
      result.push(`${selected.sectionsSelected} ${resolvePluralLabel(selected.sectionsSelected, 'section')}`);
      if (selected.sectionsIndeterminate) {
        result.push(`(${selected.subsectionsSelected} ${resolvePluralLabel(selected.subsectionsSelected, 'subsection')})`);
      }
    } else if (selected.subsectionsSelected) {
      result.push(`${selected.subsectionsSelected} ${resolvePluralLabel(selected.subsectionsSelected, 'subsection')}`);
    }
    return result;
  }, [field]);

  return (
    <FormControl error={!!fieldState.error} size='small' fullWidth margin='none' variant='outlined'>
      <InputLabel id='ongo-scope-label'>{LABEL}</InputLabel>
      <Select displayEmpty label={LABEL} labelId='ongo-scope-label' multiple renderValue={v => (v as string[]).join(' ')} value={value}>
        <List>
          <ListItem dense button onClick={onSelectAllToggle}>
            <ListItemIcon>
              <Checkbox
                size='small'
                color='primary'
                edge='start'
                checked={allSectionsState.checked}
                indeterminate={allSectionsState.indeterminate}
                tabIndex={-1}
                disableRipple
              />
            </ListItemIcon>
            <ListItemText id={'labelId'} primary={'Wszystkie działy'} />
          </ListItem>
          {sectionsData.map(section => (
            <SectionGroup commitScope={commitScope} key={`sectionGroup-${section.id}`} section={section} field={field} />
          ))}
        </List>
      </Select>
      {fieldState.error?.message ? <FormHelperText>{fieldState.error.message}</FormHelperText> : null}
    </FormControl>
  );
};

export default ScopeDropdown;
